import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { Gusto, PuntoVendita, CreaProduzionRequest, CategoriaGusto } from '../types';
import ApiService from '../services/api';
import { ricetteOfflineService } from '../services/ricetteOffline';
import { useOffline } from '../hooks/useOffline';
import OfflineStatus from '../components/OfflineStatus';
import { Send, Calendar, MapPin, ChefHat, WifiOff, Sparkles, Archive, AlertTriangle } from 'lucide-react';
import ProduzioneRicetteFlowV2, {
  RicetteV2Data,
  RicetteV2Progress
} from '../components/ProduzioneRicetteFlowV2';
import moment from 'moment';
import { PageContainer, PageHeader, Input, Select, Textarea, Alert, Spinner, LoadingState } from '../components/ui';
import {
  SectionCard,
  FormSection,
  InputGroup,
  FieldLabel,
  GustiGrid,
  GustoCard,
  GustoHeader,
  GustoName,
  GustoColor,
  SummaryCard,
  SummaryTitle,
  SummaryValue,
  SummaryUnit,
  SubmitButton
} from '../components/rilevazione/ui';

// ============================================================
// STYLED COMPONENTS specifici della pagina produzione
// (i primitivi condivisi arrivano da components/ui e components/rilevazione/ui)
// ============================================================

const GustiSection = styled.div`
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--gray-50);
  border-radius: 10px;
  border: 2px solid var(--gray-200);
  box-shadow: var(--shadow-sm);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: var(--space-sm) var(--space-md);
  }
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--gray-200);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--input-bg);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px var(--primary-100);
  }

  &:hover {
    border-color: var(--primary-500);
  }
`;

const FilterInfo = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
  }
`;

const ResetFilterButton = styled.button`
  background: var(--error);
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.85);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-top: 0;
  margin-bottom: 1rem;
`;

const QuantityControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  flex: 1;
`;

const IncrementSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem;
  background: var(--success-soft);
  border-radius: 8px;
`;

const DecrementSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.5rem;
  background: var(--error-soft);
  border-radius: 8px;
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--primary-50);
  border-radius: 10px;
  border: 2px solid var(--primary-200);
  min-height: 80px;
  position: relative;
`;

const IncrementButton = styled.button<{ $variant: 'small' | 'medium' | 'large' }>`
  ${props => {
    switch (props.$variant) {
      case 'small':
        return `
          width: 60px;
          height: 35px;
          font-size: 0.8rem;
          border: 2px solid var(--success);
          color: var(--success);
        `;
      case 'large':
        return `
          width: 60px;
          height: 35px;
          font-size: 1rem;
          border: 2px solid var(--accent-600);
          color: var(--accent-600);
        `;
      case 'medium':
      default:
        return `
          width: 60px;
          height: 35px;
          font-size: 0.9rem;
          border: 2px solid var(--primary-600);
          color: var(--primary-600);
        `;
    }
  }}

  background: var(--card-bg);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    ${props => {
    switch (props.$variant) {
      case 'small':
        return `background: var(--success); color: white;`;
      case 'large':
        return `background: var(--accent-600); color: white;`;
      case 'medium':
      default:
        return `background: var(--primary-600); color: white;`;
    }
  }}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DecrementButton = styled.button<{ $variant: 'small' | 'medium' | 'large' }>`
  width: 60px;
  height: 35px;
  font-size: ${props => (props.$variant === 'small' ? '0.8rem' : props.$variant === 'large' ? '1rem' : '0.9rem')};
  border: 2px solid var(--error);
  color: var(--error);
  background: var(--card-bg);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background: var(--error);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.div`
  font-size: 1.4rem;
  font-weight: bold;
  color: var(--primary-700);
  min-width: 80px;
  text-align: center;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    button {
      justify-content: center;
    }
  }
`;

const RicetteButton = styled.button`
  background: var(--accent-600);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: var(--accent-700);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AiButton = styled.button`
  background: var(--gray-700);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--gray-800);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

// Badge "Modalità Offline" accanto al titolo
const OfflineBadge = styled.span`
  font-size: 0.8rem;
  color: var(--warning);
  background: var(--warning-soft);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

// Modale di successo
const SuccessModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const SuccessContent = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  width: 100%;
  box-shadow: var(--shadow-lg);
  animation: modalSlideIn 0.3s ease-out;

  @media (max-width: 768px) {
    padding: var(--space-xl) var(--space-lg);
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-50px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const RicetteModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  max-width: 98vw;
  width: 98vw;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  animation: modalSlideIn 0.2s ease-out;

  @media (max-width: 768px) {
    padding: var(--space-md);
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const RicetteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 3px solid var(--gray-800);
  padding-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const RicetteTitle = styled.h2`
  color: var(--text-primary);
  margin: 0;
  font-size: 1.8rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: var(--gray-800);
  border: none;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: var(--gray-900);
    color: white;
  }
`;

const RicetteSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
`;

const RicettaGustoCard = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.5rem;
  border: 3px solid var(--gray-800);
  box-shadow: var(--shadow-lg);

  @media (max-width: 768px) {
    padding: var(--space-md);
  }
`;

const RicettaGustoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--gray-800);
`;

const IngredienteRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0.5rem;
  border-bottom: 2px solid var(--gray-200);
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(odd) {
    background: var(--gray-50);
    border-radius: 6px;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--success);
`;

const SuccessTitle = styled.h2`
  color: var(--success);
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SuccessDetails = styled.div`
  background: var(--gray-50);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
`;

const SuccessDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 1.1rem;

  &:last-child {
    margin-bottom: 0;
  }

  .label {
    color: var(--text-secondary);
    font-weight: 500;
  }

  .value {
    color: var(--success);
    font-weight: 700;
    font-size: 1.2rem;
  }
`;

const SuccessButton = styled.button`
  background: var(--success);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.2s;

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
  }
`;

// Componente overlay giacenza precedente
const GiacenzaOverlay = styled.div`
  background: var(--primary-700);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: var(--shadow-md);

  @media (max-width: 768px) {
    padding: var(--space-md);
  }
`;

const GiacenzaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
`;

const GiacenzaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
`;

const GiacenzaItem = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 0.75rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(10px);
`;

const GiacenzaNome = styled.span`
  font-weight: 500;
  font-size: 0.9rem;
`;

const GiacenzaQty = styled.span`
  font-weight: bold;
  font-size: 1rem;
`;

interface GiacenzaOverlayData {
  data: string;
  kg_totale: number;
  gusti: Array<{
    gusto_id: number;
    gusto_nome: string;
    quantita_kg: number;
  }>;
}

interface ProduzioneDraftState {
  data: string;
  puntoVenditaId: number | null;
  filterCategoria: string;
  quantita: { [key: number]: number };
  note: string;
}

const EMPTY_RICETTE_PROGRESS: RicetteV2Progress = {
  signature: '',
  currentIndex: 0,
  checkedKeys: [],
  checkedByGusto: {}
};

const getProduzioneDraftStorageKey = (tenantId?: number, userId?: number) =>
  `gelato_produzione_draft_v2_t${tenantId ?? 'default'}_u${userId ?? 'default'}`;

const getRicetteProgressStorageKey = (tenantId?: number, userId?: number) =>
  `gelato_produzione_ricette_progress_v3_t${tenantId ?? 'default'}_u${userId ?? 'default'}`;

const safeReadJson = <T,>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch (error) {
    console.warn(`Impossibile leggere ${key} da localStorage`, error);
    return null;
  }
};

const normalizeRicetteCheckedByGusto = (progress?: Partial<RicetteV2Progress> | null): Record<string, string[]> => {
  if (progress?.checkedByGusto && typeof progress.checkedByGusto === 'object') {
    return Object.fromEntries(
      Object.entries(progress.checkedByGusto)
        .map(([gustoId, keys]) => [gustoId, Array.from(new Set((keys || []).filter(Boolean)))])
        .filter(([, keys]) => keys.length > 0)
    ) as Record<string, string[]>;
  }

  return (progress?.checkedKeys || []).reduce<Record<string, string[]>>((accumulator, rawKey) => {
    const separatorIndex = rawKey.indexOf(':');
    if (separatorIndex <= 0) return accumulator;

    const gustoId = rawKey.slice(0, separatorIndex);
    const scopedKey = rawKey.slice(separatorIndex + 1);
    if (!scopedKey) return accumulator;

    const currentKeys = accumulator[gustoId] || [];
    if (!currentKeys.includes(scopedKey)) {
      accumulator[gustoId] = [...currentKeys, scopedKey];
    }

    return accumulator;
  }, {});
};

const flattenRicetteCheckedByGusto = (checkedByGusto: Record<string, string[]>) =>
  Object.entries(checkedByGusto).flatMap(([gustoId, keys]) =>
    keys.map((scopedKey) => `${gustoId}:${scopedKey}`)
  );

const getRicetteCheckedCount = (progress: RicetteV2Progress) => {
  if (progress.checkedByGusto) {
    return Object.values(progress.checkedByGusto).reduce((total, keys) => total + keys.length, 0);
  }

  return progress.checkedKeys.length;
};

const getRicetteSignature = (draft: Pick<ProduzioneDraftState, 'data' | 'puntoVenditaId' | 'quantita'>) => {
  const gusti = Object.entries(draft.quantita)
    .filter(([_, qty]) => qty > 0)
    .map(([gustoId, qty]) => ({
      gusto_id: Number(gustoId),
      quantita_kg: qty
    }))
    .sort((left, right) => left.gusto_id - right.gusto_id);

  return JSON.stringify({
    data: draft.data,
    puntoVenditaId: draft.puntoVenditaId,
    gusti
  });
};

const abbreviateTasteName = (name: string) => {
  const cleaned = name.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '???';

  const words = cleaned.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
  }

  return cleaned.slice(0, 3).toUpperCase();
};

const ProduzionePage: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [data, setData] = useState(moment().format('YYYY-MM-DD'));
  const [puntoVenditaId, setPuntoVenditaId] = useState<number | null>(null);
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [categorieGusti, setCategorieGusti] = useState<CategoriaGusto[]>([]);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [quantita, setQuantita] = useState<{ [key: number]: number }>({});
  const [note, setNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastProduzione, setLastProduzione] = useState<{
    kg_totale: number;
    num_gusti: number;
    id: number;
    data: string;
    punto_vendita_nome?: string;
  } | null>(null);

  // Stati per la modale delle ricette
  const [showRicetteModal, setShowRicetteModal] = useState(false);
  const [showRicetteFlowV2, setShowRicetteFlowV2] = useState(false);
  const [ricetteData, setRicetteData] = useState<RicetteV2Data | null>(null);
  const [loadingRicette, setLoadingRicette] = useState(false);
  const [ricettaIndex, setRicettaIndex] = useState(0);
  const [ingredientiSpuntati, setIngredientiSpuntati] = useState<Set<string>>(new Set());
  const [ricetteV2Progress, setRicetteV2Progress] = useState<RicetteV2Progress>(EMPTY_RICETTE_PROGRESS);
  const [draftHydrated, setDraftHydrated] = useState(false);

  // Stati per giacenza precedente (overlay)
  const [giacenzaPrecedente, setGiacenzaPrecedente] = useState<GiacenzaOverlayData | null>(null);
  const [loadingGiacenza, setLoadingGiacenza] = useState(false);

  // Hook per gestione offline
  const { isOnline } = useOffline();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  useEffect(() => {
    // Carica giacenza precedente quando cambiano data e punto vendita
    if (data && puntoVenditaId) {
      loadGiacenzaPrecedente();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al cambio di data/punto vendita; loadGiacenzaPrecedente è ricreata ad ogni render
  }, [data, puntoVenditaId]);

  useEffect(() => {
    if (!draftHydrated || !user) return;

    const draftKey = getProduzioneDraftStorageKey(user.tenant_id, user.id);
    const progressKey = getRicetteProgressStorageKey(user.tenant_id, user.id);

    const nextDraft: ProduzioneDraftState = {
      data,
      puntoVenditaId,
      filterCategoria,
      quantita,
      note
    };

    localStorage.setItem(draftKey, JSON.stringify(nextDraft));
    localStorage.setItem(progressKey, JSON.stringify(ricetteV2Progress));
  }, [data, draftHydrated, filterCategoria, note, puntoVenditaId, quantita, ricetteV2Progress, user]);

  const loadGiacenzaPrecedente = async () => {
    if (!data || !puntoVenditaId) return;

    try {
      setLoadingGiacenza(true);
      // Calcola data precedente
      const d = new Date(data);
      d.setDate(d.getDate() - 1);
      const dataPrecedente = moment(d).format('YYYY-MM-DD');

      const stockPrecedente = await ApiService.getStockCumulativo({ data: dataPrecedente, puntoVenditaId });
      setGiacenzaPrecedente(
        stockPrecedente.stock.length > 0
          ? {
              data: dataPrecedente,
              kg_totale: stockPrecedente.kg_totale,
              gusti: stockPrecedente.stock.map((item) => ({
                gusto_id: item.gusto_id,
                gusto_nome: item.gusto_nome,
                quantita_kg: item.stock_totale
              }))
            }
          : null
      );
    } catch (error: any) {
      // Non è un errore se non c'è giacenza precedente
      setGiacenzaPrecedente(null);
    } finally {
      setLoadingGiacenza(false);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [gustiData, puntiVenditaData, categorieData] = await Promise.all([
        ApiService.getGusti(),
        ApiService.getPuntiVendita(),
        ApiService.getCategorieGusti()
      ]);

      setGusti(gustiData);
      setPuntiVendita(puntiVenditaData);
      setCategorieGusti(categorieData);
      const draftKey = getProduzioneDraftStorageKey(user?.tenant_id, user?.id);
      const progressKey = getRicetteProgressStorageKey(user?.tenant_id, user?.id);
      const savedDraft = safeReadJson<ProduzioneDraftState>(draftKey);
      const savedProgress = safeReadJson<RicetteV2Progress>(progressKey);

      if (savedDraft) {
        setData(moment().format('YYYY-MM-DD')); // Forza la data odierna per sicurezza
        setPuntoVenditaId(savedDraft.puntoVenditaId ?? null);
        setFilterCategoria(savedDraft.filterCategoria || '');
        setQuantita(savedDraft.quantita || {});
        setNote(savedDraft.note || '');
      } else if (user?.punto_vendita_id) {
        setPuntoVenditaId(user.punto_vendita_id);
      } else if (puntiVenditaData.length === 1) {
        setPuntoVenditaId(puntiVenditaData[0].id);
      }

      if (savedProgress) {
        const checkedByGusto = normalizeRicetteCheckedByGusto(savedProgress);

        setRicetteV2Progress({
          signature: savedProgress.signature || '',
          currentIndex: typeof savedProgress.currentIndex === 'number' ? savedProgress.currentIndex : 0,
          checkedKeys: flattenRicetteCheckedByGusto(checkedByGusto),
          checkedByGusto
        });
      }

    } catch (error) {
      console.error('Errore caricamento dati:', error);
      setError('Errore durante il caricamento dei dati');
    } finally {
      setIsLoading(false);
      setDraftHydrated(true);
    }
  };

  const updateQuantita = (gustoId: number, delta: number) => {
    setQuantita(prev => {
      const current = prev[gustoId] || 0;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [gustoId]: newValue };
    });
  };

  const getTotaleKg = () => {
    return Object.values(quantita).reduce((total, qty) => total + qty, 0);
  };

  const getGustiSelezionati = () => {
    return Object.entries(quantita)
      .filter(([_, qty]) => qty > 0)
      .map(([gustoId, qty]) => ({
        gusto_id: parseInt(gustoId),
        quantita_kg: qty
      }));
  };

  const getCurrentDraftState = (): ProduzioneDraftState => ({
    data,
    puntoVenditaId,
    filterCategoria,
    quantita,
    note
  });

  const getCurrentRicetteSignature = () => getRicetteSignature(getCurrentDraftState());

  const enrichRicetteData = (response: RicetteV2Data): RicetteV2Data => ({
    ...response,
    dettagli_per_gusto: (response.dettagli_per_gusto || []).map((ricettaGusto) => {
      const gustoConfig = gusti.find((item) => item.id === ricettaGusto.gusto_id);

      return {
        ...ricettaGusto,
        note_produzione: gustoConfig?.note_produzione
      };
    })
  });

  const resetRicetteV2Progress = (signature = '') => {
    setRicetteV2Progress({
      signature,
      currentIndex: 0,
      checkedKeys: [],
      checkedByGusto: {}
    });
  };

  const loadRicetteData = async () => {
    const gustiSelezionati = getGustiSelezionati();

    if (gustiSelezionati.length === 0) {
      setError('Seleziona almeno un gusto per calcolare le ricette');
      return null;
    }

    setLoadingRicette(true);
    setError('');

    try {
      let response: RicetteV2Data;

      if (isOnline) {
        // Online: usa API normale
        response = await ApiService.calcolaRicette(gustiSelezionati);
      } else {
        // Offline: usa servizio offline
        response = await ricetteOfflineService.calcolaRicetteOffline(gustiSelezionati);
      }

      const enrichedResponse = enrichRicetteData(response);
      setRicetteData(enrichedResponse);
      return enrichedResponse;
    } catch (err: any) {
      // Se online ma API fallisce, prova offline come fallback
      if (isOnline && err.message) {
        try {
          console.log('[Ricette] API fallita, provo offline come fallback...');
          const offlineResponse = await ricetteOfflineService.calcolaRicetteOffline(gustiSelezionati);
          const enrichedOfflineResponse = enrichRicetteData(offlineResponse);
          setRicetteData(enrichedOfflineResponse);
          setError('Ricette calcolate offline (API non disponibile)');
          return enrichedOfflineResponse;
        } catch (offlineErr) {
          console.error('[Ricette] Anche offline fallito:', offlineErr);
        }
      }

      setError(err.message || 'Errore nel calcolo delle ricette');
      return null;
    } finally {
      setLoadingRicette(false);
    }
  };

  const handleCalcolaRicette = async () => {
    const response = await loadRicetteData();
    if (!response) return;

    setRicettaIndex(0);
    setIngredientiSpuntati(new Set());
    setShowRicetteModal(true);
  };

  const handleOpenRicetteFlowV2 = async () => {
    const signature = getCurrentRicetteSignature();
    const hasCachedRicetteForCurrentDraft = ricetteData && ricetteV2Progress.signature === signature && signature !== '';

    if (!hasCachedRicetteForCurrentDraft) {
      const response = await loadRicetteData();
      if (!response) return;

      if (ricetteV2Progress.signature !== signature) {
        resetRicetteV2Progress(signature);
      }
    }

    setShowRicetteFlowV2(true);
  };

  const handleAiForecast = async () => {
    if (!puntoVenditaId) {
      setError('Seleziona un punto vendita per generare la previsione');
      return;
    }

    try {
      setIsSubmitting(true); // Uso isSubmitting per mostrare loading
      const response = await ApiService.getDailyForecast(puntoVenditaId, data);

      // Il backend restituisce { previsione: [...] } con gusti[].gusto_id e gusti[].kg_suggeriti
      const suggerimenti = response.previsione || response.suggerimenti || [];

      if (suggerimenti.length > 0) {
        const newQuantita = { ...quantita };
        let count = 0;
        let totaleApplicato = 0;

        suggerimenti.forEach((sugg: any) => {
          if (sugg.gusto_id && sugg.kg_suggeriti > 0) {
            const kg = parseFloat(sugg.kg_suggeriti);
            newQuantita[sugg.gusto_id] = kg;
            totaleApplicato += kg;
            count++;
          }
        });

        setQuantita(newQuantita);
        const totaleKg = totaleApplicato > 0 ? ` (${totaleApplicato.toFixed(1)} kg totali)` : '';
        const noteAi = response.note_ai ? `\n${response.note_ai}` : '';
        setSuccess(`Previsione AI generata. Aggiornate quantità per ${count} gusti${totaleKg}${noteAi}`);
      } else {
        setError('Nessun suggerimento disponibile per questa data/punto vendita.');
      }
    } catch (err: any) {
      console.error('Errore previsione AI:', err);
      setError(err.response?.data?.error || 'Errore durante la generazione della previsione AI');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredGusti = gusti.filter(gusto => {
    const matchesCategory = filterCategoria === '' || gusto.categoria === filterCategoria;
    return matchesCategory;
  });
  const currentRicetteSignature = getCurrentRicetteSignature();
  const canResumeRicetteV2 =
  currentRicetteSignature !== '' &&
    ricetteV2Progress.signature === currentRicetteSignature &&
    (ricetteV2Progress.currentIndex > 0 || getRicetteCheckedCount(ricetteV2Progress) > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!puntoVenditaId) {
      setError('Seleziona un punto vendita');
      return;
    }

    const gustiSelezionati = getGustiSelezionati();
    if (gustiSelezionati.length === 0) {
      setError('Aggiungi almeno un gusto alla produzione');
      return;
    }

    try {
      setIsSubmitting(true);

      const produzione: CreaProduzionRequest = {
        data,
        puntoVenditaId,
        gusti: gustiSelezionati,
        note: note.trim()
      };

      const response = await ApiService.createProduzione(produzione);

      // Mostra la modale di successo con i dati della produzione
      const puntoVenditaSelezionato = puntiVendita.find(pv => pv.id === puntoVenditaId);
      setLastProduzione({
        kg_totale: response.kg_totale,
        num_gusti: gustiSelezionati.length,
        id: response.id,
        data: data,
        punto_vendita_nome: puntoVenditaSelezionato?.nome
      });
      setShowSuccessModal(true);

      // Reset form
      setQuantita({});
      setNote('');
      setFilterCategoria('');
      setRicetteData(null);
      setShowRicetteFlowV2(false);
      resetRicetteV2Progress();

      localStorage.removeItem(getProduzioneDraftStorageKey(user?.tenant_id, user?.id));
      localStorage.removeItem(getRicetteProgressStorageKey(user?.tenant_id, user?.id));

    } catch (error: any) {
      console.error('Errore invio produzione:', error);
      setError(
        error.response?.data?.error ||
        'Errore durante l\'invio della produzione'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setLastProduzione(null);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  if (showRicetteFlowV2 && ricetteData) {
    return (
      <PageContainer>
        <OfflineStatus />
        <ProduzioneRicetteFlowV2
          data={ricetteData}
          progress={ricetteV2Progress}
          onProgressChange={setRicetteV2Progress}
          onBack={() => setShowRicetteFlowV2(false)}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <OfflineStatus />

      <PageHeader
        title={(
          <>
            Registra Produzione
            {!isOnline && (
              <OfflineBadge>
                <WifiOff size={14} />
                Modalità Offline
              </OfflineBadge>
            )}
          </>
        )}
      />

      {error && <Alert $variant="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <SectionCard>
          <FormSection>
            <InputGroup>
              <FieldLabel>
                <Calendar size={16} />
                Data Produzione
              </FieldLabel>
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <FieldLabel>
                <MapPin size={16} />
                Punto Vendita
              </FieldLabel>
              <Select
                value={puntoVenditaId || ''}
                onChange={(e) => setPuntoVenditaId(parseInt(e.target.value))}
                required
              >
                <option value="">Seleziona punto vendita</option>
                {puntiVendita.map(pv => (
                  <option key={pv.id} value={pv.id}>
                    {pv.nome}
                  </option>
                ))}
              </Select>
            </InputGroup>
          </FormSection>
        </SectionCard>

        {/* Overlay stock residuo precedente */}
        {giacenzaPrecedente && !loadingGiacenza && (
          <GiacenzaOverlay>
            <GiacenzaHeader>
              <Archive size={24} />
              <span>Stock residuo al {moment(data).subtract(1, 'day').format('DD/MM/YYYY')}</span>
            </GiacenzaHeader>
            <GiacenzaGrid>
              {giacenzaPrecedente.gusti?.map((g) => (
                <GiacenzaItem key={g.gusto_id}>
                  <GiacenzaNome>{g.gusto_nome}</GiacenzaNome>
                  <GiacenzaQty>{g.quantita_kg} kg</GiacenzaQty>
                </GiacenzaItem>
              ))}
            </GiacenzaGrid>
            <div style={{ marginTop: '1rem', fontSize: '0.95rem', opacity: 0.9 }}>
              <strong>Totale: {giacenzaPrecedente.kg_totale} kg</strong>
            </div>
          </GiacenzaOverlay>
        )}

        <SectionCard>
          <GustiSection>
            <SectionTitle>Gusti Prodotti</SectionTitle>

            <FilterSection>
              <FilterLabel>
                Filtra per categoria:
              </FilterLabel>
              <FilterSelect
                value={filterCategoria}
                onChange={(e) => setFilterCategoria(e.target.value)}
              >
                <option value="">Tutte le categorie</option>
                {categorieGusti.filter(cat => cat.attivo).map(cat => (
                  <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                ))}
              </FilterSelect>
              <FilterInfo>
                {filteredGusti.length} gusti disponibili
                {filterCategoria && (
                  <>
                    <span style={{ fontWeight: 'bold', color: 'var(--primary-700)' }}>
                      • {filterCategoria}
                    </span>
                    <ResetFilterButton
                      onClick={() => setFilterCategoria('')}
                      title="Mostra tutti i gusti"
                    >
                      ×
                    </ResetFilterButton>
                  </>
                )}
              </FilterInfo>
            </FilterSection>

            <GustiGrid>
              {filteredGusti.map(gusto => (
                <GustoCard
                  key={gusto.id}
                  className={quantita[gusto.id] > 0 ? 'selected' : ''}
                >
                  <GustoHeader>
                    <GustoName>
                      <GustoColor color={gusto.colore} />
                      <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                        {gusto.categoria}
                      </span>
                    </GustoName>
                  </GustoHeader>

                  <QuantityControl>
                    {/* Sezione Incrementi */}
                    <IncrementSection>
                      <IncrementButton
                        $variant="small"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, 0.5)}
                        title="Aggiungi 0.5 kg"
                      >
                        +0.5
                      </IncrementButton>

                      <IncrementButton
                        $variant="medium"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, 2)}
                        title="Aggiungi 2 kg"
                      >
                        +2
                      </IncrementButton>

                      <IncrementButton
                        $variant="large"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, 4)}
                        title="Aggiungi 4 kg"
                      >
                        +4
                      </IncrementButton>
                    </IncrementSection>

                    {/* Sezione Centrale - Info Gusto */}
                    <CenterSection>
                      <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        {gusto.nome}
                      </div>
                      <QuantityDisplay>
                        {quantita[gusto.id] || 0} kg
                      </QuantityDisplay>
                    </CenterSection>

                    {/* Sezione Decrementi */}
                    <DecrementSection>
                      <DecrementButton
                        $variant="small"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, -0.5)}
                        disabled={!quantita[gusto.id] || quantita[gusto.id] <= 0}
                        title="Rimuovi 0.5 kg"
                      >
                        -0.5
                      </DecrementButton>

                      <DecrementButton
                        $variant="medium"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, -2)}
                        disabled={!quantita[gusto.id] || quantita[gusto.id] < 2}
                        title="Rimuovi 2 kg"
                      >
                        -2
                      </DecrementButton>

                      <DecrementButton
                        $variant="large"
                        type="button"
                        onClick={() => updateQuantita(gusto.id, -4)}
                        disabled={!quantita[gusto.id] || quantita[gusto.id] < 4}
                        title="Rimuovi 4 kg"
                      >
                        -4
                      </DecrementButton>
                    </DecrementSection>
                  </QuantityControl>
                </GustoCard>
              ))}
            </GustiGrid>
          </GustiSection>
        </SectionCard>

        <SectionCard>
          <InputGroup>
            <FieldLabel>
              Note (opzionale)
            </FieldLabel>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aggiungi note sulla produzione (es. particolarità, eventi speciali, etc.)"
              maxLength={500}
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {note.length}/500 caratteri
            </small>
          </InputGroup>
        </SectionCard>

        <SummaryCard>
          <SummaryTitle>Totale Produzione</SummaryTitle>
          <SummaryValue>
            {getTotaleKg()} <SummaryUnit>kg</SummaryUnit>
          </SummaryValue>
          <div>
            {getGustiSelezionati().length} gusti selezionati
            {filterCategoria && (
              <span style={{ opacity: 0.8, fontSize: '0.9rem' }}>
                {' '}(categoria: {filterCategoria})
              </span>
            )}
          </div>
        </SummaryCard>

        <ActionButtonsContainer>
          <AiButton
            type="button"
            onClick={handleAiForecast}
            disabled={isSubmitting || !puntoVenditaId}
            title="Genera previsione con AI"
          >
            {isSubmitting ? <Spinner $size={18} /> : <Sparkles size={18} />}
            Previsione AI
          </AiButton>

          <RicetteButton
            type="button"
            disabled={loadingRicette || getTotaleKg() === 0}
            onClick={handleCalcolaRicette}
            style={{
              position: 'relative',
              background: !isOnline ? 'var(--gray-500)' : undefined
            }}
          >
            {loadingRicette ? (
              <Spinner $size={18} />
            ) : (
              <>
                <ChefHat size={16} />
                Visualizza Ricette
                {!isOnline && (
                  <span style={{
                    fontSize: '0.7rem',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '0.1rem 0.4rem',
                    borderRadius: '8px',
                    marginLeft: '0.5rem'
                  }}>
                    OFFLINE
                  </span>
                )}
              </>
            )}
          </RicetteButton>

          <RicetteButton
            type="button"
            disabled={loadingRicette || getTotaleKg() === 0}
            onClick={handleOpenRicetteFlowV2}
            style={{
              background: 'var(--primary-800)'
            }}
          >
            {loadingRicette ? (
              <Spinner $size={18} />
            ) : (
              <>
                <ChefHat size={16} />
                {canResumeRicetteV2 ? 'Riprendi Ricette V2' : 'Apri Ricette V2'}
              </>
            )}
          </RicetteButton>

          <SubmitButton type="submit" disabled={isSubmitting || getTotaleKg() === 0}>
            {isSubmitting ? (
              <Spinner $size={18} />
            ) : (
              <>
                <Send size={16} />
                Invia Produzione
              </>
            )}
          </SubmitButton>
        </ActionButtonsContainer>

        <div style={{ marginTop: '-0.75rem', marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.92rem', fontWeight: 600 }}>
          <strong>Visualizza Ricette</strong> mantiene il flusso attuale. <strong>Ricette V2</strong> apre il nuovo percorso con nomi dei gusti e avanzamento persistente per il test operativo.
        </div>

        {success && (
          <Alert $variant="success">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ flex: 1 }}>{success}</span>
              <span
                onClick={() => setSuccess('')}
                style={{ cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-secondary)', padding: '0 4px', marginLeft: '8px' }}
                title="Chiudi"
              >✕</span>
            </div>
          </Alert>
        )}
      </form>

      {/* Modale di successo */}
      {showSuccessModal && lastProduzione && (
        <SuccessModal>
          <SuccessContent>
            <SuccessIcon>✓</SuccessIcon>
            <SuccessTitle>Produzione registrata</SuccessTitle>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
              La produzione è stata salvata nel sistema.
            </p>

            <SuccessDetails>
              <SuccessDetailItem>
                <span className="label">ID Produzione</span>
                <span className="value">#{lastProduzione.id}</span>
              </SuccessDetailItem>
              <SuccessDetailItem>
                <span className="label">Data Produzione</span>
                <span className="value">{new Date(lastProduzione.data).toLocaleDateString('it-IT')}</span>
              </SuccessDetailItem>
              {lastProduzione.punto_vendita_nome && (
                <SuccessDetailItem>
                  <span className="label">Punto Vendita</span>
                  <span className="value">{lastProduzione.punto_vendita_nome}</span>
                </SuccessDetailItem>
              )}
              <SuccessDetailItem>
                <span className="label">Numero Gusti</span>
                <span className="value">{lastProduzione.num_gusti}</span>
              </SuccessDetailItem>
              <SuccessDetailItem>
                <span className="label">Totale Kg</span>
                <span className="value">{lastProduzione.kg_totale} kg</span>
              </SuccessDetailItem>
            </SuccessDetails>

            <SuccessButton onClick={closeSuccessModal}>
              Continua
            </SuccessButton>
          </SuccessContent>
        </SuccessModal>
      )}

      {/* Modale delle ricette */}
      {showRicetteModal && ricetteData && (
        <SuccessModal>
          <RicetteModalContent>
            <RicetteHeader>
              <RicetteTitle>
                <ChefHat size={24} />
                Ricette di Produzione
                {ricetteData?.offline && (
                  <span style={{
                    fontSize: '0.7rem',
                    background: 'var(--gray-500)',
                    color: 'white',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '12px',
                    marginLeft: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}>
                    <WifiOff size={12} />
                    OFFLINE
                  </span>
                )}
              </RicetteTitle>
              <RicetteSummary style={{ marginLeft: 'auto', marginRight: '1rem' }}>
                {ricetteData.num_gusti} gusti • {ricetteData.kg_totali} kg totali
                {(ricetteData.num_gusti_senza_ricette || 0) > 0 && (
                  <div style={{ fontSize: '0.85em', color: 'var(--warning)', fontWeight: 'bold' }}>
                    {ricetteData.num_gusti_senza_ricette} gusti senza ricette
                  </div>
                )}
              </RicetteSummary>
              <CloseButton onClick={() => setShowRicetteModal(false)}>
                ×
              </CloseButton>
            </RicetteHeader>

            {/* Banner informativo offline */}
            {ricetteData?.offline && (
              <div style={{
                background: 'var(--gray-600)',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                margin: '0 0 1rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <WifiOff size={20} />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Modalità Laboratorio Offline
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                    Solo ingredienti disponibili - {ricetteData.message || 'Costi non calcolati offline'}
                  </div>
                </div>
              </div>
            )}

            {/* Vista una ricetta alla volta */}
            {(() => {
              const gustiList: any[] = ricetteData.dettagli_per_gusto || [];
              if (gustiList.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)', background: 'var(--gray-50)', borderRadius: '12px', border: '2px dashed var(--gray-200)' }}>
                    <div style={{ marginBottom: '1rem' }}><AlertTriangle size={40} /></div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem' }}>Nessuna ricetta trovata</div>
                    <div style={{ fontSize: '1.1rem' }}>I gusti selezionati non hanno ricette configurate nel sistema.</div>
                  </div>
                );
              }
              const gusto = gustiList[ricettaIndex];
              const totale = gustiList.length;
              return (
                <div>
                  {/* Barra navigazione */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', gap: '1rem' }}>
                    <button
                      onClick={() => {
                        setRicettaIndex(i => Math.max(0, i - 1));
                        setIngredientiSpuntati(new Set());
                      }}
                      disabled={ricettaIndex === 0}
                      style={{
                        fontSize: '2rem',
                        fontWeight: 900,
                        padding: '0.4rem 1.2rem',
                        borderRadius: '10px',
                        border: '3px solid var(--gray-800)',
                        background: ricettaIndex === 0 ? 'var(--gray-200)' : 'var(--gray-800)',
                        color: ricettaIndex === 0 ? 'var(--gray-400)' : 'white',
                        cursor: ricettaIndex === 0 ? 'default' : 'pointer',
                        lineHeight: 1,
                        flexShrink: 0
                      }}
                    >‹</button>

                    {/* Navigazione gusti con sigla nominale */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', flex: 1 }}>
                      {gustiList.map((g: any, i: number) => (
                        <button
                          key={g.gusto_id}
                          onClick={() => { setRicettaIndex(i); setIngredientiSpuntati(new Set()); }}
                          title={g.gusto_nome}
                          style={{
                            minWidth: i === ricettaIndex ? '78px' : '56px',
                            height: '32px',
                            borderRadius: '999px',
                            border: '2px solid var(--gray-800)',
                            background: i === ricettaIndex ? 'var(--gray-800)' : 'white',
                            color: i === ricettaIndex ? 'white' : 'var(--gray-800)',
                            cursor: 'pointer',
                            padding: '0 12px',
                            transition: 'all 0.2s',
                            fontSize: '0.75rem',
                            fontWeight: 900,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase'
                          }}
                        >
                          {abbreviateTasteName(g.gusto_nome)}
                        </button>
                      ))}
                    </div>

                    {/* Contatore */}
                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)', flexShrink: 0 }}>
                      {ricettaIndex + 1} / {totale}
                    </span>

                    <button
                      onClick={() => {
                        setRicettaIndex(i => Math.min(totale - 1, i + 1));
                        setIngredientiSpuntati(new Set());
                      }}
                      disabled={ricettaIndex === totale - 1}
                      style={{
                        fontSize: '2rem',
                        fontWeight: 900,
                        padding: '0.4rem 1.2rem',
                        borderRadius: '10px',
                        border: '3px solid var(--gray-800)',
                        background: ricettaIndex === totale - 1 ? 'var(--gray-200)' : 'var(--gray-800)',
                        color: ricettaIndex === totale - 1 ? 'var(--gray-400)' : 'white',
                        cursor: ricettaIndex === totale - 1 ? 'default' : 'pointer',
                        lineHeight: 1,
                        flexShrink: 0
                      }}
                    >›</button>
                  </div>

                  {/* Card gusto corrente */}
                  <RicettaGustoCard style={{
                    borderColor: gusto.ha_ricette ? 'var(--gray-800)' : 'var(--warning)',
                    background: gusto.ha_ricette ? 'var(--card-bg)' : 'var(--error-soft)'
                  }}>
                    <RicettaGustoHeader>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <div style={{
                          width: '32px', height: '32px', flexShrink: 0,
                          borderRadius: '50%',
                          backgroundColor: gusto.gusto_colore || 'var(--gray-300)',
                          border: '3px solid var(--gray-800)'
                        }} />
                        <span style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--gray-900)', letterSpacing: '-0.5px', lineHeight: 1.1 }}>
                          {gusto.gusto_nome}
                        </span>
                        {!gusto.ha_ricette && (
                          <span style={{ background: 'var(--warning)', color: 'white', fontSize: '0.85rem', padding: '4px 10px', borderRadius: '6px', fontWeight: 800, flexShrink: 0 }}>
                            SENZA RICETTE
                          </span>
                        )}
                      </div>
                      <div style={{ background: gusto.ha_ricette ? 'var(--gray-800)' : 'var(--warning)', color: 'white', padding: '8px 20px', borderRadius: '10px', fontSize: '2rem', fontWeight: 900, lineHeight: 1, flexShrink: 0 }}>
                        {gusto.quantita_kg} kg
                      </div>
                    </RicettaGustoHeader>

                    {/* Hint spunta */}
                    {gusto.ha_ricette && (
                      <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                        Tocca un ingrediente per segnarlo come aggiunto
                      </div>
                    )}

                    {/* Lista ingredienti */}
                    {gusto.ha_ricette ? (
                      <div>
                        {(() => {
                          const ingredientiMiscela = gusto.ingredienti.filter((ing: any) => !ing.is_variegatura && !ing.is_aggiunta);
                          const ingredientiVariegatura = gusto.ingredienti.filter((ing: any) => ing.is_variegatura);
                          const ingredientiAggiunta = gusto.ingredienti.filter((ing: any) => ing.is_aggiunta);
                          const haSezioni = ingredientiVariegatura.length > 0 || ingredientiAggiunta.length > 0;

                          const renderIngrediente = (ing: any, i: number) => {
                            const key = `${gusto.gusto_id}-${ing.ingrediente_id}-${ing.is_variegatura ? 'var' : (ing.is_aggiunta ? 'agg' : 'misc')}-${i}`;
                            const spuntato = ingredientiSpuntati.has(key);
                            return (
                              <IngredienteRow
                                key={key}
                                onClick={() => setIngredientiSpuntati(prev => {
                                  const next = new Set(prev);
                                  if (next.has(key)) next.delete(key); else next.add(key);
                                  return next;
                                })}
                                style={{
                                  cursor: 'pointer',
                                  opacity: spuntato ? 0.38 : 1,
                                  background: spuntato ? 'var(--gray-200)' : undefined,
                                  borderRadius: spuntato ? '8px' : undefined,
                                  userSelect: 'none',
                                  transition: 'opacity 0.15s, background 0.15s'
                                }}
                              >
                                {/* Checkbox visuale */}
                                <div style={{
                                  width: '28px', height: '28px', flexShrink: 0,
                                  borderRadius: '6px',
                                  border: `3px solid ${spuntato ? 'var(--success)' : 'var(--gray-400)'}`,
                                  background: spuntato ? 'var(--success)' : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  marginRight: '10px',
                                  fontSize: '1.1rem', color: 'white', fontWeight: 900
                                }}>
                                  {spuntato ? '✓' : ''}
                                </div>
                                {/* Nome ingrediente */}
                                <div style={{
                                  fontSize: '1.25rem',
                                  fontWeight: 700,
                                  color: spuntato ? 'var(--gray-500)' : 'var(--gray-900)',
                                  flex: 1,
                                  minWidth: 0,
                                  textDecoration: spuntato ? 'line-through' : 'none'
                                }}>
                                  {ing.ingrediente_nome}
                                </div>
                                {/* Calcolo */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                  <span style={{ color: 'var(--gray-400)', fontSize: '1rem', fontWeight: 500 }}>
                                    {ing.quantita_per_kg} {ing.unita_ricetta}/kg × {gusto.quantita_kg} kg =
                                  </span>
                                  <span style={{
                                    fontSize: '1.8rem',
                                    fontWeight: 900,
                                    color: spuntato ? 'var(--gray-400)' : 'var(--gray-900)',
                                    background: spuntato ? 'var(--gray-200)' : 'var(--gray-100)',
                                    border: `2px solid ${spuntato ? 'var(--gray-400)' : 'var(--gray-800)'}`,
                                    padding: '2px 12px',
                                    borderRadius: '8px',
                                    minWidth: '90px',
                                    textAlign: 'right',
                                    display: 'inline-block',
                                    textDecoration: spuntato ? 'line-through' : 'none'
                                  }}>
                                    {ing.quantita_necessaria}<span style={{ fontSize: '0.9rem', fontWeight: 600, marginLeft: '4px' }}>{ing.unita_ricetta}</span>
                                  </span>
                                </div>
                              </IngredienteRow>
                            );
                          };

                          return (
                            <>
                              {/* Sezione Miscela */}
                              {haSezioni && ingredientiMiscela.length > 0 && (
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  Miscela / Gelato
                                </div>
                              )}
                              {ingredientiMiscela.map(renderIngrediente)}

                              {/* Sezione Variegatura */}
                              {ingredientiVariegatura.length > 0 && (
                                <>
                                  <div style={{
                                    fontSize: '1rem', fontWeight: 800, color: 'var(--accent-600)',
                                    marginTop: '1rem', marginBottom: '0.5rem',
                                    paddingTop: '0.75rem', borderTop: '2px dashed var(--warning)',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                  }}>
                                    Variegatura
                                  </div>
                                  {ingredientiVariegatura.map(renderIngrediente)}
                                </>
                              )}

                              {/* Sezione Aggiunta */}
                              {ingredientiAggiunta.length > 0 && (
                                <>
                                  <div style={{
                                    fontSize: '1rem', fontWeight: 800, color: 'var(--info)',
                                    marginTop: '1rem', marginBottom: '0.5rem',
                                    paddingTop: '0.75rem', borderTop: '2px dashed var(--info)',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                  }}>
                                    Aggiunta
                                  </div>
                                  {ingredientiAggiunta.map(renderIngrediente)}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--warning)', fontWeight: 700, fontSize: '1.1rem', background: 'var(--error-soft)', borderRadius: '8px', border: '2px dashed var(--warning)' }}>
                        <div style={{ marginBottom: '0.5rem' }}><AlertTriangle size={28} /></div>
                        <div>Nessuna ricetta configurata per questo gusto</div>
                        <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', fontWeight: 500 }}>Vai in "Gestione Gusti" per aggiungere gli ingredienti</div>
                      </div>
                    )}
                  </RicettaGustoCard>

                  {/* Footer navigazione + chiudi */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid var(--gray-200)', gap: '1rem' }}>
                    <button
                      onClick={() => { setRicettaIndex(i => Math.max(0, i - 1)); setIngredientiSpuntati(new Set()); }}
                      disabled={ricettaIndex === 0}
                      style={{
                        fontSize: '1.1rem', fontWeight: 700, padding: '0.75rem 1.5rem',
                        borderRadius: '10px', border: '3px solid var(--gray-800)',
                        background: ricettaIndex === 0 ? 'var(--gray-200)' : 'var(--gray-800)',
                        color: ricettaIndex === 0 ? 'var(--gray-400)' : 'white',
                        cursor: ricettaIndex === 0 ? 'default' : 'pointer'
                      }}
                    >‹ Precedente</button>

                    <button
                      style={{ background: 'var(--gray-600)', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '10px', cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700 }}
                      onClick={() => setShowRicetteModal(false)}
                    >✕ Chiudi</button>

                    <button
                      onClick={() => { setRicettaIndex(i => Math.min(totale - 1, i + 1)); setIngredientiSpuntati(new Set()); }}
                      disabled={ricettaIndex === totale - 1}
                      style={{
                        fontSize: '1.1rem', fontWeight: 700, padding: '0.75rem 1.5rem',
                        borderRadius: '10px', border: '3px solid var(--gray-800)',
                        background: ricettaIndex === totale - 1 ? 'var(--gray-200)' : 'var(--gray-800)',
                        color: ricettaIndex === totale - 1 ? 'var(--gray-400)' : 'white',
                        cursor: ricettaIndex === totale - 1 ? 'default' : 'pointer'
                      }}
                    >Successivo ›</button>
                  </div>
                </div>
              );
            })()}
          </RicetteModalContent>
        </SuccessModal>
      )}
    </PageContainer>
  );
};

export default ProduzionePage;
